import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardList, Send, Plus, Trash2, Copy, Check, ExternalLink, Eye,
  ChevronLeft, X,
} from "lucide-react";
import api from "../../lib/api";

const TYPE_OPTIONS = [
  { v: "text", l: "Texte court" },
  { v: "textarea", l: "Paragraphe" },
  { v: "email", l: "Email" },
  { v: "phone", l: "Téléphone" },
  { v: "number", l: "Nombre" },
  { v: "select", l: "Liste déroulante" },
  { v: "multiselect", l: "Choix multiples" },
  { v: "radio", l: "Choix unique" },
];

const STATUS_PILL = {
  pending: { label: "Envoyé", color: "var(--gai-grey-500)" },
  viewed: { label: "Consulté", color: "var(--gai-red)" },
  completed: { label: "Complété", color: "#0F9D58" },
};

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="gai-btn-ghost text-xs"
      data-testid={testid}
    >
      {done ? <Check size={12} /> : <Copy size={12} />} {done ? "Copié" : "Copier le lien"}
    </button>
  );
}

/* ---------- Template editor ---------- */
function TemplateEditor({ initial, onCancel, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [intro, setIntro] = useState(initial?.intro || "");
  const [questions, setQuestions] = useState(initial?.questions || []);
  const [busy, setBusy] = useState(false);

  const addQ = () => {
    setQuestions((arr) => [
      ...arr,
      { id: `q_${Math.random().toString(36).slice(2, 7)}`, label: "", type: "text", required: false, options: [] },
    ]);
  };

  const updateQ = (i, patch) => {
    setQuestions((arr) => arr.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  };

  const removeQ = (i) => setQuestions((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        intro: intro.trim(),
        questions: questions.map((q) => ({
          id: q.id || `q_${Math.random().toString(36).slice(2, 7)}`,
          label: q.label || "",
          type: q.type || "text",
          required: !!q.required,
          options: q.options || [],
          help: q.help || "",
        })),
      };
      if (initial?.id) {
        await api.patch(`/admin/questionnaire-templates/${initial.id}`, payload);
      } else {
        await api.post("/admin/questionnaire-templates", payload);
      }
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="gai-card gai-sharp p-6 space-y-5" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline mb-1">{initial?.id ? "Modifier le modèle" : "Nouveau modèle"}</div>
          <h3 className="serif text-2xl text-[var(--gai-black)]">Composition du questionnaire</h3>
        </div>
        <button onClick={onCancel} className="gai-btn-ghost text-xs" data-testid="template-editor-cancel">
          <ChevronLeft size={12} /> Retour
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <input
          className="gai-input"
          placeholder="Nom du modèle (ex. Brief annonceur)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="template-name"
        />
        <input
          className="gai-input"
          placeholder="Description courte (interne)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <textarea
        className="gai-input resize-none"
        rows={3}
        placeholder="Introduction adressée au client (visible en haut du questionnaire)"
        value={intro}
        onChange={(e) => setIntro(e.target.value)}
        data-testid="template-intro"
      />

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Questions ({questions.length})</div>
          <button onClick={addQ} className="gai-btn-outline text-xs" data-testid="template-add-question">
            <Plus size={12} /> Ajouter
          </button>
        </div>

        <div className="space-y-3">
          {questions.length === 0 && (
            <div className="text-sm text-[var(--gai-grey-500)] italic">Aucune question pour le moment.</div>
          )}
          {questions.map((q, i) => (
            <div key={i} className="border p-4 space-y-3" style={{ borderColor: "var(--gai-grey-200)" }} data-testid={`template-question-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 grid md:grid-cols-3 gap-3">
                  <input
                    className="gai-input md:col-span-2"
                    placeholder="Libellé de la question"
                    value={q.label}
                    onChange={(e) => updateQ(i, { label: e.target.value })}
                  />
                  <select
                    className="gai-input"
                    value={q.type}
                    onChange={(e) => updateQ(i, { type: e.target.value, options: e.target.value.match(/select|radio|multiselect/) ? (q.options.length ? q.options : ["Option 1"]) : [] })}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{o.l}</option>
                    ))}
                  </select>
                </div>
                <button onClick={() => removeQ(i)} className="gai-btn-ghost text-red-300 hover:text-red-200 p-2" aria-label="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>

              {(q.type === "select" || q.type === "multiselect" || q.type === "radio") && (
                <textarea
                  className="gai-input resize-none text-sm"
                  rows={2}
                  placeholder="Options — une par ligne"
                  value={(q.options || []).join("\n")}
                  onChange={(e) => updateQ(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                />
              )}
              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-2 text-[var(--gai-grey-700)]">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(i, { required: e.target.checked })} />
                  Obligatoire
                </label>
                <input
                  className="gai-input flex-1"
                  placeholder="Aide / précision (optionnel)"
                  value={q.help || ""}
                  onChange={(e) => updateQ(i, { help: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
        <button onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
        <button onClick={save} disabled={busy || !name.trim()} className="gai-btn-gold text-xs" data-testid="template-save">
          {busy ? "Enregistrement…" : "Enregistrer le modèle"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Send invite modal ---------- */
function SendInviteModal({ templates, onClose, onSent }) {
  const [form, setForm] = useState({
    template_id: templates[0]?.id || "",
    client_name: "", client_email: "", client_company: "", client_phone: "",
    custom_intro: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/questionnaire-invites", form);
      setLink(data.public_url);
      onSent();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="w-full max-w-2xl gai-card gai-sharp p-7 relative" data-testid="invite-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-gold)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">Envoyer un questionnaire</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Inviter un client</h3>

        {!link ? (
          <form onSubmit={submit} className="space-y-4">
            <select required className="gai-input" value={form.template_id}
              onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="invite-template">
              {templates.map((t) => (
                <option key={t.id} value={t.id} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{t.name}</option>
              ))}
            </select>
            <div className="grid md:grid-cols-2 gap-3">
              <input required type="email" className="gai-input" placeholder="Email du client"
                value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                data-testid="invite-email" />
              <input className="gai-input" placeholder="Nom du contact"
                value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <input className="gai-input" placeholder="Société"
                value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })} />
              <input className="gai-input" placeholder="Téléphone (optionnel)"
                value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
            </div>
            <textarea rows={3} className="gai-input resize-none"
              placeholder="Mot d'introduction personnalisé (optionnel)"
              value={form.custom_intro} onChange={(e) => setForm({ ...form, custom_intro: e.target.value })} />
            <textarea rows={2} className="gai-input resize-none"
              placeholder="Notes internes (non visibles par le client)"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="gai-btn-ghost text-xs">Annuler</button>
              <button disabled={busy} className="gai-btn-gold text-xs" data-testid="invite-submit">
                {busy ? "Génération…" : (<> <Send size={12} /> Générer le lien d'invitation </>)}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4" data-testid="invite-success">
            <div className="overline" style={{ color: "var(--gai-gold)" }}>Invitation prête</div>
            <p className="text-sm text-[var(--gai-grey-700)]">
              Copiez le lien ci-dessous et envoyez-le au client par email, WhatsApp ou tout autre canal de votre choix.
            </p>
            <div className="border p-4 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--gai-gold-dim)" }}>
              <code className="text-xs flex-1 break-all text-[var(--gai-grey-800)]">{link}</code>
              <CopyBtn text={link} testid="invite-copy-link" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <a href={link} target="_blank" rel="noreferrer" className="gai-btn-ghost text-xs">
                <ExternalLink size={12} /> Aperçu
              </a>
              <button onClick={onClose} className="gai-btn-gold text-xs" data-testid="invite-done">
                Terminé
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Response viewer ---------- */
function ResponseModal({ invite, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/questionnaire/${invite.token}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [invite.token]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="w-full max-w-3xl gai-card gai-sharp p-7 relative max-h-[90vh] overflow-y-auto" data-testid="response-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-gold)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">Réponses du client</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-1">{invite.client_name || invite.client_email}</h3>
        <div className="text-xs text-[var(--gai-grey-500)] mb-5">
          {invite.client_email}{invite.client_company ? ` · ${invite.client_company}` : ""} · {invite.template_name}
        </div>

        {!data && <div className="text-sm text-[var(--gai-grey-500)]">Chargement…</div>}
        {data?.error && <div className="text-sm text-red-300">Impossible de charger les réponses.</div>}

        {data?.template && (
          <dl className="space-y-4">
            {data.template.questions.map((q) => {
              const v = data.invite.answers?.[q.id];
              return (
                <div key={q.id} className="border-l-2 pl-4" style={{ borderColor: "var(--gai-red)" }}>
                  <dt className="text-[0.68rem] tracking-[0.18em] uppercase text-[var(--gai-gold)]">{q.label}</dt>
                  <dd className="text-[var(--gai-grey-900)] mt-1 whitespace-pre-line">
                    {Array.isArray(v) ? v.join(", ") : (v || <span className="text-[var(--gai-grey-400)]">— Pas de réponse —</span>)}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function Questionnaires() {
  const [tab, setTab] = useState("invites"); // invites | templates
  const [templates, setTemplates] = useState([]);
  const [invites, setInvites] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | template object
  const [showSend, setShowSend] = useState(false);
  const [viewingInvite, setViewingInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const [t, i] = await Promise.all([
      api.get("/admin/questionnaire-templates").then((r) => r.data).catch(() => []),
      api.get("/admin/questionnaire-invites").then((r) => r.data).catch(() => []),
    ]);
    setTemplates(t);
    setInvites(i);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const counts = useMemo(() => ({
    pending: invites.filter((x) => x.status === "pending").length,
    viewed: invites.filter((x) => x.status === "viewed").length,
    completed: invites.filter((x) => x.status === "completed").length,
  }), [invites]);

  const deleteTemplate = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    await api.delete(`/admin/questionnaire-templates/${id}`);
    refresh();
  };
  const deleteInvite = async (id) => {
    if (!window.confirm("Supprimer cette invitation ?")) return;
    await api.delete(`/admin/questionnaire-invites/${id}`);
    refresh();
  };

  if (editing) {
    return (
      <TemplateEditor
        initial={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    );
  }

  return (
    <div data-testid="page-admin-questionnaires">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Qualification client</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Questionnaires</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)]">
            Envoyez des questionnaires d'intégration personnalisés à vos clients & prospects, et centralisez les réponses dans la fiche CRM.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSend(true)}
            disabled={!templates.length}
            className="gai-btn-gold text-xs disabled:opacity-50"
            data-testid="btn-send-questionnaire"
          >
            <Send size={12} /> Envoyer un questionnaire
          </button>
          <button onClick={() => setEditing("new")} className="gai-btn-outline text-xs" data-testid="btn-new-template">
            <Plus size={12} /> Nouveau modèle
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Modèles", value: templates.length, testid: "kpi-templates" },
          { label: "Envoyés en attente", value: counts.pending, testid: "kpi-pending" },
          { label: "Consultés", value: counts.viewed, testid: "kpi-viewed" },
          { label: "Complétés", value: counts.completed, testid: "kpi-completed" },
        ].map((k) => (
          <div key={k.label} className="gai-card gai-sharp p-5" data-testid={k.testid}>
            <div className="serif text-3xl text-[var(--gai-black)]">{k.value}</div>
            <div className="mt-1 text-[0.68rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { k: "invites", l: `Invitations envoyées (${invites.length})` },
          { k: "templates", l: `Modèles (${templates.length})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-[0.7rem] tracking-[0.2em] uppercase border ${
              tab === t.k
                ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-gold)]"
            }`}
            data-testid={`tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Invites list */}
      {tab === "invites" && (
        <div className="gai-card gai-sharp overflow-hidden" data-testid="invites-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.68rem] tracking-[0.2em] uppercase text-[var(--gai-grey-500)]" style={{ background: "var(--gai-grey-50)" }}>
                <th className="py-3 px-5">Client</th>
                <th className="py-3 px-5">Modèle</th>
                <th className="py-3 px-5">Envoyé le</th>
                <th className="py-3 px-5">Statut</th>
                <th className="py-3 px-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-8 text-center text-[var(--gai-grey-500)]">Chargement…</td></tr>
              )}
              {!loading && invites.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-[var(--gai-grey-500)]">
                  <ClipboardList className="mx-auto mb-3 opacity-50" size={28} />
                  Aucun questionnaire envoyé pour le moment.
                </td></tr>
              )}
              {invites.map((inv) => {
                const pill = STATUS_PILL[inv.status] || STATUS_PILL.pending;
                const link = `${window.location.origin}/questionnaire/${inv.token}`;
                return (
                  <tr key={inv.id} className="border-t" style={{ borderColor: "var(--gai-grey-200)" }} data-testid={`invite-row-${inv.id}`}>
                    <td className="py-3 px-5">
                      <div className="text-[var(--gai-black)] font-medium">{inv.client_name || inv.client_email}</div>
                      <div className="text-xs text-[var(--gai-grey-500)] mt-0.5">{inv.client_email}{inv.client_company ? ` · ${inv.client_company}` : ""}</div>
                    </td>
                    <td className="py-3 px-5 text-[var(--gai-grey-700)]">{inv.template_name || "—"}</td>
                    <td className="py-3 px-5 text-[var(--gai-grey-600)] text-xs">
                      {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="py-3 px-5">
                      <span className="text-[0.68rem] tracking-[0.18em] uppercase" style={{ color: pill.color }}>● {pill.label}</span>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {inv.status === "completed" ? (
                          <button onClick={() => setViewingInvite(inv)} className="gai-btn-ghost text-xs" data-testid={`view-${inv.id}`}>
                            <Eye size={12} /> Voir
                          </button>
                        ) : (
                          <CopyBtn text={link} testid={`copy-${inv.id}`} />
                        )}
                        <button onClick={() => deleteInvite(inv.id)} className="gai-btn-ghost text-red-300 hover:text-red-200 p-2" aria-label="Supprimer">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Templates list */}
      {tab === "templates" && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="templates-grid">
          {templates.length === 0 && (
            <div className="gai-card gai-sharp p-10 text-center md:col-span-2 text-[var(--gai-grey-500)]">
              Aucun modèle. Créez votre premier modèle.
            </div>
          )}
          {templates.map((t) => (
            <div key={t.id} className="gai-card gai-sharp p-5" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="serif text-xl text-[var(--gai-black)]">{t.name}</div>
                  {t.description && (
                    <p className="text-xs text-[var(--gai-grey-500)] mt-1 line-clamp-2">{t.description}</p>
                  )}
                  <div className="mt-3 text-[0.68rem] tracking-[0.18em] uppercase text-[var(--gai-gold)]">
                    {(t.questions || []).length} question(s)
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setEditing(t)} className="gai-btn-ghost text-xs" data-testid={`edit-${t.id}`}>Modifier</button>
                  <button onClick={() => deleteTemplate(t.id)} className="gai-btn-ghost text-xs text-red-300" data-testid={`delete-${t.id}`}>
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSend && <SendInviteModal templates={templates} onClose={() => setShowSend(false)} onSent={refresh} />}
      {viewingInvite && <ResponseModal invite={viewingInvite} onClose={() => setViewingInvite(null)} />}
    </div>
  );
}
